import type { TExample } from '@atlas/uicorn/preview/modules';
import React, { useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import cn from 'classnames';
import type { TContextMenuRef } from '@atlas/uicorn/lib/Control/ContextMenu';
import useRefCallback from 'use-ref-callback';
import useControlledState from '@atlas/tool/box/hooks/useControlledState';
import { FormContext } from '@atlas/uicorn/lib/Control/Form';
import useLastRef from '@atlas/tool/box/hooks/useLastRef';
import window from '@atlas/tool/box/browser/window';
import Label from '@atlas/uicorn/lib/Display/Label';
import Text from '@atlas/uicorn/lib/Display/Text';
import {
    SSelectClearButton,
    SSelectClearIcon,
    SSelectDropdownIndicator,
    SSelect,
    SSelectOptionsMenu,
    SSelectToggleIcon,
    downHotkey,
    enterHotkey,
    spaceHotkey,
    upHotkey,
    TSelectRef,
    TSelectOption,
    TSelectProps,
} from '@atlas/uicorn/lib/Input/Select';
import useRequest from 'use-request';
import { isEqual } from 'lodash-es';
import FormItem from '@atlas/uicorn/lib/Control/FormItem';

function SelectMultiComponent<TOption extends TSelectMultiOption = TSelectMultiOption>(
    props: TSelectMultiProps<TOption>,
    ref: React.ForwardedRef<TSelectMultiRef>,
): React.ReactElement {
    const {
        value,
        defaultValue,
        wrongValue,
        onChange,
        placeholder,
        open: propsOpen,
        defaultOpen,
        onOpen,
        disabled: propsDisabled,
        allowClear,
        className,
        style,
        options,
        dropdown,
        search,
        resolveOptions,
        selectAll,
        ...menuProps
    } = props;
    // #region Visual definitions
    const { disabled: contextDisabled } = useContext(FormContext);
    const disabled = propsDisabled ?? contextDisabled;

    const status = FormItem.useStatus();

    const [element, setElement] = useState<HTMLDivElement | null>(null);
    const elementRef = useLastRef(element);

    const [minMenuWidth, setMinMenuWidth] = useState(0);

    const updateMinMenuWidth = useRefCallback(() => setMinMenuWidth(element?.offsetWidth ?? 0));

    const [menuRef, setMenuRef] = useState<TContextMenuRef | null>(null);
    const [open, setOpen] = useControlledState(propsOpen, onOpen, defaultOpen);
    // #endregion Visual definitions

    // #region Data definitions
    const [cachedOptions, setCachedOptions] = useState(options);
    const updateOptionCache = useRefCallback((optionsToAppend: readonly TOption[]) => {
        const shouldSkipUpdate = optionsToAppend.every((ao) => {
            const found = cachedOptions?.find((co) => co.key === ao.key);
            return found && isEqual(found, ao);
        });
        if (shouldSkipUpdate) return;

        // this would discard ephemeral options with '...' label
        setCachedOptions((cached: typeof cachedOptions) =>
            optionsToAppend.concat(cached?.filter((co) => !optionsToAppend.some((o) => co.key === o.key)) ?? []),
        );
    });
    useEffect(() => options && updateOptionCache(options), [options, updateOptionCache]);

    const keysToOptions = useCallback(
        (keys: Array<TOption['key']>) =>
            keys.map((key) => {
                const foundOption = cachedOptions?.find((o) => o.key === key);
                // ephemeral option to be replaced by the async request
                return foundOption ?? ({ key, label: '...' } as TOption);
            }),
        [cachedOptions],
    );

    const normalizedValue = useMemo(() => value && keysToOptions(value), [value, keysToOptions]);
    const normalizedDefaultValue = useMemo(
        () => defaultValue && keysToOptions(defaultValue),
        [defaultValue, keysToOptions],
    );
    const [valueOptions, setValueOptions] = useControlledState<TOption[] | undefined>(
        normalizedValue,
        (options) => {
            if (!onChange) return;
            if (!options?.length) onChange([], []);
            else {
                updateOptionCache(options);

                onChange(
                    options.map((o) => o.key),
                    options,
                );
            }
        },
        normalizedDefaultValue,
    );

    const missingKeysList = useMemo(() => {
        if (!valueOptions) return [];
        return valueOptions.filter((vo) => !cachedOptions?.some((co) => co.key === vo.key)).map((vo) => vo.key);
    }, [valueOptions, cachedOptions]);
    const { value: missingOptions } = useRequest(
        () => (missingKeysList.length ? resolveOptions?.(missingKeysList) : undefined),
        [missingKeysList],
    );
    useEffect(() => missingOptions && updateOptionCache(missingOptions), [missingOptions, updateOptionCache]);
    // #endregion Data definitions

    useEffect(() => {
        updateMinMenuWidth();

        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        const listener = () => {
            if (timeoutId !== null) clearTimeout(timeoutId);
            timeoutId = setTimeout(updateMinMenuWidth, 100);
        };
        window?.addEventListener('resize', listener);
        return () => window?.removeEventListener('resize', listener);
    }, [element, valueOptions, style, className, updateMinMenuWidth]);

    useImperativeHandle(
        ref,
        () => ({
            ...menuRef,
            focus: () => elementRef.current?.focus(),
            blur: () => elementRef.current?.blur(),
        }),
        [menuRef, elementRef],
    );

    const handleKeyDown = useRefCallback<React.KeyboardEventHandler<HTMLDivElement>>((event) => {
        if (spaceHotkey.matches(event) || enterHotkey.matches(event)) {
            // Keep the default behavior for clear button
            if ((event.target as HTMLButtonElement).tagName === 'BUTTON') return;

            event.preventDefault();
            menuRef?.toggle();
            return;
        }

        if (upHotkey.matches(event) || downHotkey.matches(event)) {
            event.preventDefault();
            menuRef?.open();
            return;
        }
    });

    const handleSelect = useRefCallback(setValueOptions);

    const handleClear = useRefCallback((event: React.MouseEvent) => {
        event.preventDefault();
        setValueOptions([]);
        setOpen(false);
    });

    const typeWorkaround: any = { $minWidth: minMenuWidth };

    const preparedSearch = useMemo(() => search ?? (options && options.length > 4), [search, options]);

    const labelsContainerRef = useRef<HTMLSpanElement | null>(null);
    const hiddenLabelsCounter = useRef(0);

    useEffect(() => {
        if (!labelsContainerRef.current || !element) return;
        const label = labelsContainerRef.current.children[0];

        if (!label?.clientHeight) return;

        element.style.maxHeight = `calc(${label.clientHeight * MIN_VISIBLE_VALUES}px + var(--select-value-gap) * 4)`;
    }, [element, valueOptions]);

    const getLabels = useRefCallback((selector: string) =>
        Array.from(labelsContainerRef.current?.querySelectorAll(selector) || []),
    );

    const hasHiddenLabels = useRefCallback(() => {
        const hiddenLabelsCount = getLabels(`${SLabel}.${hiddenClassName}`).length;
        hiddenLabelsCounter.current = hiddenLabelsCount;

        const counter = labelsContainerRef.current?.querySelector(`${SOverflowCounter}`);

        if (hiddenLabelsCount) {
            counter?.classList.remove(hiddenClassName);
        } else {
            counter?.classList.add(hiddenClassName);
        }

        return hiddenLabelsCount;
    });

    const showNextLabel = useRefCallback(() => {
        const hiddenLabel = getLabels(`${SLabel}.${hiddenClassName}`)[0];

        if (hiddenLabel) {
            hiddenLabel.classList.remove(hiddenClassName);
            return true;
        }

        return false;
    });

    const fitsInContainer = useCallback(() => {
        if (!element) return false;
        return element.scrollHeight <= element.clientHeight;
    }, [element]);

    const incrementCounter = useRefCallback(() => {
        hiddenLabelsCounter.current += 1;
    });

    const hideLastLabel = useRefCallback(() => {
        const visibleLabels = getLabels(`${SLabel}:not(.${hiddenClassName})`);

        const lastVisibleLabel = visibleLabels[visibleLabels.length - 1];
        if (!lastVisibleLabel) return;

        lastVisibleLabel.classList.add(hiddenClassName);
    });

    const hideAdditionalLabels = useRefCallback(() => {
        const labels = getLabels(`${SLabel}`);

        // Last visible label may not leave space for the counter, so check it first
        const additionalLabels = labels.slice(MIN_VISIBLE_VALUES - 1, labels.length);

        additionalLabels.forEach((label) => {
            label.classList.add(hiddenClassName);
        });
    });

    useEffect(() => {
        hideAdditionalLabels();

        while (hasHiddenLabels()) {
            if (showNextLabel()) {
                if (fitsInContainer()) {
                    incrementCounter();
                } else {
                    hideLastLabel();
                    break;
                }
            }
        }
    }, [valueOptions, hideAdditionalLabels, hasHiddenLabels, showNextLabel, fitsInContainer, hideLastLabel]);

    return (
        <SSelectOptionsMenu
            {...menuProps}
            items={options}
            search={preparedSearch}
            open={disabled ? false : open}
            onOpenChange={disabled ? undefined : setOpen}
            selected={valueOptions}
            onSelect={handleSelect}
            selectAll={selectAll}
            {...typeWorkaround}
            ref={setMenuRef}
        >
            <SSelect
                className={cn(className, 'ant-input', {
                    'ant-input-status-error': status === 'error',
                })}
                style={style}
                tabIndex={0}
                onKeyDown={handleKeyDown}
                data-open={open}
                data-disabled={disabled}
                role="listbox"
                ref={setElement}
            >
                {valueOptions?.length ? (
                    <SSelectMultiValuesList ref={labelsContainerRef}>
                        {valueOptions.map((option) => (
                            <SLabel
                                key={option.key}
                                color="grey0"
                                prefix={option.icon}
                                children={option.label as string}
                                $danger={wrongValue?.includes(option.key)}
                                onRemove={
                                    disabled
                                        ? undefined
                                        : () =>
                                              setValueOptions((value) => {
                                                  const newValue = value?.filter((v) => v.key !== option.key);
                                                  return newValue ?? [];
                                              })
                                }
                            />
                        ))}
                        <SOverflowCounter
                            data-is-counter={true}
                            children={`+ ${hiddenLabelsCounter.current}`}
                            className={hiddenClassName}
                        />
                    </SSelectMultiValuesList>
                ) : (
                    <SPlaceholder children={placeholder} color="grey7" ellipsis />
                )}
                {valueOptions?.length && allowClear ? (
                    <SSelectClearButton
                        onClick={handleClear}
                        icon={
                            <>
                                <SSelectClearIcon />
                                <SSelectDropdownIndicator
                                    as={dropdown}
                                    opened={open && !disabled}
                                    disabled={disabled}
                                />
                            </>
                        }
                    />
                ) : (
                    <SSelectToggleIcon as={dropdown} opened={open && !disabled} disabled={disabled} />
                )}
            </SSelect>
        </SSelectOptionsMenu>
    );
}

const SelectMulti = React.memo(React.forwardRef(SelectMultiComponent)) as <
    TOption extends TSelectMultiOption = TSelectMultiOption,
>(
    props: TSelectMultiProps<TOption> & { ref?: React.Ref<TSelectMultiRef> },
) => React.ReactElement;

export default SelectMulti;

// #region Helpers

const MIN_VISIBLE_VALUES = 3;
const hiddenClassName = 'label_hidden';

// #endregion

// #region Styles

const SPlaceholder = styled(Text)`
    color: var(--input-placeholder-color);
`;

const SSelectMultiValuesList = styled.span`
    display: inline-flex;
    flex-wrap: wrap;
    gap: inherit;
    overflow-x: auto;
    align-self: flex-start;
    width: 100%;

    ${(p) => p.theme.patch.noScroll}
`;

const SOverflowCounter = styled(Label).attrs({
    color: 'grey0',
})`
    &.${hiddenClassName} {
        display: none;
    }
`;

const SLabel = styled(Label)<{ $danger?: boolean }>`
    &.${hiddenClassName} {
        display: none;
    }

    ${(p) =>
        p.$danger &&
        css`
            border: 0.1rem solid ${p.theme.color.red6};
        `}
`;

// #endregion

// #region Types

export type TSelectMultiRef = TSelectRef;

export type TSelectMultiOption = TSelectOption;

export type TSelectMultiProps<TOption extends TSelectMultiOption = TSelectMultiOption> = Omit<
    TSelectProps<TOption>,
    'onChange' | 'defaultValue' | 'value'
> & {
    value?: TOption['key'][];
    defaultValue?: TOption['key'][];
    wrongValue?: TOption['key'][];
    onChange?: ((value: TOption['key'][], option: TOption[]) => void) & ((value: undefined, option: undefined) => void);
    resolveOptions?: (keys: TOption['key'][]) => Promise<TOption[]>;
};

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        return (
            <SelectMulti
                key={props.defaultValue}
                defaultValue={
                    props.defaultValue === 'Option A'
                        ? ['a']
                        : props.defaultValue === 'Option B'
                        ? ['b']
                        : props.defaultValue === 'Option A & B'
                        ? ['a', 'b']
                        : undefined
                }
                options={[
                    { key: 'a', label: 'Option A', icon: '1️⃣' },
                    { key: 'b', label: 'B is an option', icon: '2️⃣' },
                    { key: 'c', label: 'Option C', icon: '3️⃣' },
                    { key: 'd', label: 'Option D', icon: '4️⃣' },
                    { key: 'e', label: 'Option E', icon: '5️⃣' },
                    { key: 'f', label: 'Option F', icon: '6️⃣' },
                    { key: 'g', label: 'Option G', icon: '7️⃣' },
                    { key: 'h', label: 'Option H', icon: '8️⃣' },
                    { key: 'i', label: 'Option I', icon: '9️⃣' },
                    { key: 'j', label: 'Option J', icon: '🔟' },
                ]}
                placeholder={props.placeholder}
                disabled={props.disabled}
                search={props.search}
                allowClear={props.allowClear}
                onChange={props.onChange}
                style={props.fixedWidth ? { width: `${props.fixedWidth}px` } : undefined}
                selectAll={props.selectAll}
            />
        );
    },
    controls: {
        placeholder: 'string',
        disabled: 'boolean',
        search: 'boolean',
        allowClear: 'boolean',
        fixedWidth: { type: 'number', initial: 400, connected: false },
        defaultValue: ['Not set', 'Option A', 'Option B', 'Option A & B'],
        selectAll: 'boolean',
    },
    handlers: {
        onChange: 'console',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System----2.0?type=design&node-id=52-5474&mode=design&t=8rXmz6Q0cKMTeQF5-0',
};

export function ControlledExample() {
    const [value, setValue] = useState<string[] | undefined>(undefined);
    return (
        <SelectMulti
            defaultValue={['a', 'b']}
            options={[
                { key: 'a', label: 'Option A', icon: '1️⃣' },
                { key: 'b', label: 'Option B', icon: '2️⃣' },
                { key: 'c', label: 'Option C', icon: '3️⃣' },
                { key: 'd', label: 'Option D', icon: '4️⃣' },
                { key: 'e', label: 'Option E', icon: '5️⃣' },
            ]}
            placeholder="Placeholder"
            value={value}
            onChange={setValue}
            allowClear
            style={{ width: 300 }}
        />
    );
}

export function ControlledAsyncSearchExample() {
    const [value, setValue] = useState<string[] | undefined>(['c', 'e']);

    const options = [
        { key: 'a', label: 'Option A', icon: '1️⃣' },
        { key: 'b', label: 'Option B', icon: '2️⃣' },
        { key: 'c', label: 'Option C', icon: '3️⃣' },
        { key: 'd', label: 'Option D', icon: '4️⃣' },
        { key: 'e', label: 'Option E', icon: '5️⃣' },
    ];

    const resolveOptions = useRefCallback(async (keys: string[]): Promise<Array<typeof options[number]>> => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(options.filter((option) => keys.includes(option.key)));
            }, 1000);
        });
    });

    const asyncSearch = useRefCallback(async (query: string): Promise<Array<typeof options[number]>> => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase())));
            }, 1000);
        });
    });

    return (
        <SelectMulti<typeof options[number]>
            placeholder="Placeholder"
            value={value}
            onChange={setValue}
            search={asyncSearch}
            resolveOptions={resolveOptions}
            allowClear
            style={{ width: 300 }}
        />
    );
}

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect, vi, beforeAll, afterEach } = import.meta.vitest;
    describe('SelectMulti', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        beforeAll(function mockAddEventListener() {
            vi.mock('@atlas/tool/box/browser/window', () => {
                // Could not reset mock that is set in a different file
                return { default: {} };
            });
            if (window) {
                Object.assign(window, { addEventListener: vi.fn() });
                Object.assign(window, { removeEventListener: vi.fn() });
            }
        });

        afterEach(function resetState() {
            vi.resetAllMocks();
        });

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
